import { LineDemo } from "../components/LineDemo";
import { SeoContent } from "../components/SeoContent";
import { seoPageForPath } from "../../shared/seo/pages";
import type { CellMark } from "../../shared/nonogram/types";

/**
 * The technique pages. Their prose lives in shared/seo/pages.ts, which is what the
 * build pre-renders, so this component reads the heading and lede from there rather
 * than restating them — two copies of a page's first sentence is exactly the drift
 * the static shell exists to prevent.
 *
 * What React adds on top is the part a crawler does not need and a reader does: a
 * line you can walk through one deduction at a time.
 */

type Demo = { length: number; clues: number[]; caption: string; known?: CellMark[] };

/** Squares a crossing line has already settled, written as the square numbers a
    reader would count on the page rather than as array indexes. */
function settled(length: number, filled: number[], crossed: number[] = []): CellMark[] {
  return Array.from({ length }, (_, index) =>
    filled.includes(index + 1) ? 1 : crossed.includes(index + 1) ? -1 : 0,
  );
}

const DEMOS: Record<string, Demo[]> = {
  "/nonogram/techniques": [
    { length: 10, clues: [4, 3], caption: "Ten squares, clues 4 3" },
  ],
  "/nonogram/techniques/overlap": [
    { length: 10, clues: [7], caption: "One long clue" },
    { length: 10, clues: [3, 2], caption: "The same line, clues that pay nothing" },
  ],
  "/nonogram/techniques/edge": [
    { length: 15, clues: [5, 4], caption: "A filled square at the left edge", known: settled(15, [1]) },
    { length: 10, clues: [7], caption: "The same 7, with square 1 crossed out", known: settled(10, [], [1]) },
  ],
  "/nonogram/techniques/gaps": [
    { length: 12, clues: [5], caption: "A segment too short for the clue", known: settled(12, [], [4]) },
    { length: 15, clues: [4, 6], caption: "One cross, and the line has nowhere left to slide", known: settled(15, [], [4]) },
    { length: 15, clues: [2, 6], caption: "The same cross, clues the short segment can hold", known: settled(15, [], [4]) },
  ],
  "/nonogram/techniques/forcing": [
    { length: 15, clues: [1, 1, 6], caption: "Only one run can reach square 15", known: settled(15, [15]) },
    { length: 12, clues: [2, 5], caption: "A square that two runs can both reach", known: settled(12, [6]) },
  ],
};

export function Techniques({ path }: { path: string }) {
  const page = seoPageForPath(path);
  if (!page) return null;
  const demos = DEMOS[path] ?? [];

  return (
    <main className="prose-page technique-page" id="main-content">
      <p className="eyebrow">{page.eyebrow}</p>
      <h1>{page.heading}</h1>
      <p className="lede">{page.lede}</p>

      {demos.length > 0 && (
        <div className="line-demo-stack" aria-label="Worked lines">
          {demos.map((demo) => (
            <LineDemo clues={demo.clues} caption={demo.caption} key={demo.caption} known={demo.known} length={demo.length} />
          ))}
        </div>
      )}

      <SeoContent path={path} />
    </main>
  );
}
